import { Injectable, inject } from '@angular/core'

import type { Observable } from 'rxjs'
import { catchError, filter, map, of, switchMap, take } from 'rxjs'

import { ApiService } from '../../api/api.service'
import type { ListInvoiceDto } from '../../api/responses'
import type {
  BillingCycleDto,
  OrganizationByIdDto,
  OrganizationDto,
} from '../../api/responses/organization'
import { ORGANIZATION_BRANCHES } from '../../shared/constants/organization-branches'
import { AuthService } from '../auth/auth.service'

export interface OrganizationFilterItem {
  id: number
  name: string
}

export interface OrganizationInvoicePrices {
  organizationId: number
  currentInvoicePrice: number
  scheduledInvoicePrice: number
}

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private readonly apiService = inject(ApiService)
  private readonly authService = inject(AuthService)

  getOrganizationsForReseller(): Observable<OrganizationDto[]> {
    return this.authService.currentUser$.pipe(
      switchMap(user => {
        const organizationId = user?.organization?.id
        if (!organizationId) throw new Error('missing organization')
        return this.apiService.getOrganizationsForReseller(organizationId)
      }),
      map(response => response.data?.Organizations.docs ?? [])
    )
  }

  getOrganization(): Observable<OrganizationByIdDto | null> {
    return this.authService.currentUser$.pipe(
      switchMap(user => {
        const organizationId = user?.organization?.id
        if (!organizationId) throw new Error('missing organization')
        return this.apiService.getOrganizationById(organizationId)
      }),
      map(response => response.data?.Organization ?? null)
    )
  }

  getBillingCycles(
    filterOrganizationId?: number | null
  ): Observable<BillingCycleDto[]> {
    return this.getOrganizationId(filterOrganizationId).pipe(
      switchMap(organizationId =>
        this.apiService.getBillingCyclesByOrganization(organizationId)
      ),
      map(response => response.data.BillingCycles.docs),
      catchError(() => of([]))
    )
  }

  /**
   * @param id The numeric ID of the organization to update.
   * @param organization An Organization ojbect containing the fields to update.
   * @returns An observable emitting the Organization details or null if not
   *   found/error.
   */
  updateOrganizationById(
    id: number,
    organization: Partial<OrganizationDto>
  ): Observable<OrganizationByIdDto | null> {
    return this.apiService.updateOrganizationById(id, organization).pipe(
      map(result => {
        if (!result.data) {
          throw new Error('Failed to update organization: No data returned')
        }

        return result.data.updateOrganization
      })
    )
  }

  updateBillingCycleReference(
    billingCycleId: number,
    reference: string | null
  ): Observable<BillingCycleDto | null> {
    return this.apiService
      .updateBillingCycleById(billingCycleId, { reference })
      .pipe(
        map(result => {
          if (!result.data) {
            throw new Error('Failed to update billing cycle: No data returned')
          }

          return result.data.updateBillingCycle
        })
      )
  }

  createOrganization(
    data: Partial<OrganizationDto>
  ): Observable<OrganizationDto | null> {
    return this.apiService.createOrganization(data).pipe(
      map(result => {
        if (!result.data) {
          throw new Error('Failed to create organization: No data returned')
        }

        return result.data.createOrganization
      })
    )
  }

  getBranchValues(): Observable<string[]> {
    return of([...ORGANIZATION_BRANCHES])
  }

  getInvoicesForOrganization(): Observable<ListInvoiceDto[]> {
    return this.apiService
      .getInvoicesByOrganization()
      .pipe(map(result => result.data.Invoices.docs))
  }

  getOrganizationInvoicePrices(): Observable<Set<OrganizationInvoicePrices>> {
    return this.apiService.getOrganizationInvoicePrices().pipe(
      map(result => {
        const dto = result.data.organizationInvoicePrices

        const entries = dto.map(entry => ({
          organizationId: entry.organizationId,
          currentInvoicePrice: entry.currentInvoicePrice,
          scheduledInvoicePrice: entry.scheduledInvoicePrice,
        }))

        return new Set(entries)
      })
    )
  }

  // A page of the reseller its organisations whose name matches the search term.
  getOrganizationFilterPage(
    search: string,
    page: number,
    limit: number
  ): Observable<{ items: OrganizationFilterItem[]; hasNextPage: boolean }> {
    return this.authService.currentUser$.pipe(
      filter(user => user !== undefined),
      take(1),
      switchMap(user => {
        const organizationId = user?.organization?.id
        if (!organizationId) return of({ items: [], hasNextPage: false })

        return this.apiService
          .getOrganizationFilterItems(organizationId, page, limit, search)
          .pipe(
            map(({ data }) => ({
              items: data.Organizations.docs.map(dto => ({
                id: dto.id,
                name: dto.name,
              })),
              hasNextPage:
                data.Organizations.page < data.Organizations.totalPages,
            }))
          )
      })
    )
  }

  getOrganizationFilterItemsWithSelf(): Observable<
    Set<OrganizationFilterItem>
  > {
    return this.authService.currentUser$.pipe(
      switchMap(user => {
        const selfOrganization = user?.organization
        const selfOrganizationId = selfOrganization?.id
        const selfOrganizationName = selfOrganization?.name

        // A user without an organization simply has nothing to filter on.
        // Throwing here would break every view that reads this list, including
        // the ones that exist to offer creating an organization.
        if (!selfOrganizationId || !selfOrganizationName) {
          return of(new Set<OrganizationFilterItem>())
        }

        return this.apiService
          .getOrganizationFilterItems(selfOrganizationId)
          .pipe(
            map(result => result.data.Organizations.docs),
            map(docs => {
              const organizations: OrganizationFilterItem[] = [
                {
                  id: selfOrganizationId,
                  name: selfOrganizationName,
                },
              ]

              organizations.push(
                ...docs.map(dto => ({
                  id: dto.id,
                  name: dto.name,
                }))
              )

              return new Set(organizations)
            })
          )
      })
    )
  }

  private getOrganizationId(
    filterOrganizationId?: number | null
  ): Observable<number> {
    if (typeof filterOrganizationId === 'number') {
      return of(filterOrganizationId)
    }

    return this.authService.currentUser$.pipe(
      map(user => user?.organization?.id),
      map(organizationId => {
        if (!organizationId) throw new Error('missing organization')

        return organizationId
      })
    )
  }
}
